/**
 * Turns an action's (or emulator operation's) input schema into form fields
 * and the request body, so the Run dialog needs no per-action code.
 *
 * @module features/sandboxes/components/v2/detail/actionForm
 */
import type { SandboxV2DeclaredAction, SandboxV2InputProp, SandboxV2Operation } from '../../../hooks/useSandboxV2Detail';

export type ActionField = {
  name: string;
  type: SandboxV2InputProp['type'];
  description?: string;
  required: boolean;
  sensitive: boolean;
  options?: Array<string | number>;
  default?: string | number | boolean;
};

/** An action or operation, reduced to what the list and the dialog show. */
export type RunnableAction = {
  /** The `action` the run endpoint takes: a slug, or `<plugin>.<op>`. */
  ref: string;
  description: string;
  fields: ActionField[];
};

export function declaredActionToRunnable(action: SandboxV2DeclaredAction): RunnableAction {
  return {
    ref: action.slug,
    description: action.description,
    fields: Object.entries(action.input).map(([name, prop]) => ({
      name,
      type: prop.type,
      description: prop.description,
      required: prop.required ?? false,
      sensitive: prop.sensitive ?? false,
      options: prop.enum,
      default: prop.default,
    })),
  };
}

export function operationToRunnable(operation: SandboxV2Operation): RunnableAction {
  const required = new Set(operation.inputSchema.required ?? []);
  return {
    ref: operation.name,
    description: operation.description,
    fields: Object.entries(operation.inputSchema.properties ?? {}).map(([name, prop]) => ({
      name,
      // Array/object/anyOf inputs fall back to a text field (the only operation today, email.sent-emails, takes `{}`).
      type: prop.type === 'integer' || prop.type === 'number' || prop.type === 'boolean' ? prop.type : 'string',
      description: prop.description,
      required: required.has(name),
      sensitive: false,
      options: prop.enum,
      default: typeof prop.default === 'string' || typeof prop.default === 'number' || typeof prop.default === 'boolean' ? prop.default : undefined,
    })),
  };
}

/** Form state per field: text for string-ish and number, a boolean for boolean fields. */
export type FormValues = Record<string, string | boolean>;

export function initialFormValues(fields: ActionField[], personas: string[]): FormValues {
  const values: FormValues = {};
  for (const field of fields) {
    if (field.type === 'boolean') values[field.name] = field.default === true;
    else if (field.default !== undefined) values[field.name] = String(field.default);
    else if (field.type === 'persona' && field.required && personas.length > 0) values[field.name] = personas[0]!;
    else values[field.name] = '';
  }
  return values;
}

/**
 * The request `input`, or the names of required fields still empty. Empty
 * optional fields are omitted so the API applies their defaults.
 */
export function buildRunInput(
  fields: ActionField[],
  values: FormValues
): { input: Record<string, string | number | boolean> } | { missing: string[]; invalid: string[] } {
  const input: Record<string, string | number | boolean> = {};
  const missing: string[] = [];
  const invalid: string[] = [];
  for (const field of fields) {
    const raw = values[field.name];
    if (field.type === 'boolean') {
      input[field.name] = raw === true;
      continue;
    }
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (text === '') {
      if (field.required) missing.push(field.name);
      continue;
    }
    if (field.type === 'number' || field.type === 'integer') {
      const parsed = Number(text);
      if (!Number.isFinite(parsed)) {
        invalid.push(`${field.name} must be a number`);
        continue;
      }
      if (field.type === 'integer' && !Number.isInteger(parsed)) {
        invalid.push(`${field.name} must be a whole number`);
        continue;
      }
      input[field.name] = parsed;
    } else if (field.options && field.options.some((option) => typeof option === 'number')) {
      const match = field.options.find((option) => String(option) === text);
      input[field.name] = match ?? text;
    } else {
      input[field.name] = field.type === 'string' ? (typeof raw === 'string' ? raw : text) : text;
    }
  }
  return missing.length > 0 || invalid.length > 0 ? { missing, invalid } : { input };
}
